// Client-side caseId generator.
// REQUIRED: AAD = caseId on encryptPayload (D-23, CRY-04). Client must know
// caseId BEFORE encrypting, so generation happens here, not on the server.
// Server uses the client-provided caseId verbatim.
//
// LT-01 (2026-07-29) — O ID NÃO CARREGA TEMPO. A versão anterior era
// `Date.now().toString(36)` + hex do randomUUID: os 10 primeiros chars ERAM o
// instante do envio, reversíveis com um `parseInt(_, 36)`. A UI mostra a semana
// (`createdWeek`) de propósito — granularidade semanal é a defesa anti-correlação
// —, mas o próprio identificador entregava h:min:s:ms. Impresso no PDF que o
// denunciante guarda, ecoado no status, exibido truncado no painel do comitê
// (onde `slice(0,12)` mostrava quase só o timestamp) e usado em nome de arquivo
// de PDF baixado. Cruzado com ponto/crachá/câmera, estreita 1.300 pessoas para
// "quem estava no prédio às 13:38:13".
//
// Desenho atual: 26 símbolos sorteados uniformemente de um alfabeto de 32.
//
//   - **130 bits** de entropia (26 × log2(32)). A versão antiga tinha 60 bits
//     efetivos de aleatoriedade — não 64: o nibble de versão do UUIDv4 (o '4'
//     literal no índice 12) é fixo.
//   - **Zero viés de módulo**: 32 divide 256, então `byte & 31` é uniforme.
//     Contraste deliberado com `generateProtocolo` (IntakeWizard.tsx), que usa
//     `% 32` sobre o mesmo tamanho de alfabeto — ali também é sem viés, mas o
//     padrão `% N` só é seguro quando N é potência de 2. Não replicar com % 36.
//   - **Alfabeto Crockford base32** (0-9 A-Z menos I, L, O, U): sem os pares
//     ambíguos na leitura humana, e todo símbolo é `[0-9A-Z]`, então o id segue
//     passando no regex do servidor (`/^[0-9A-Z]{20,32}$/`, denuncia/index.ts e
//     attachments.ts) — nenhuma mudança de contrato, nenhuma migration.
//
// Colisão: com 130 bits, 10^6 denúncias dão P ≈ 4×10^-28. A PK do Postgres
// (`case_id text PRIMARY KEY`) rejeita duplicata com 23505 de todo modo — falha
// fechada, nunca sobrescrita.
//
// FORWARD-ONLY: caseIds já emitidos são IMUTÁVEIS (o caseId é a AAD do
// ciphertext e o servidor não tem a DEK). Este gerador só afeta casos novos.
//
// ⚠️ Coberto por guard COMPORTAMENTAL em `case-id.test.ts` — relógio congelado
// não pode colapsar prefixo, relógio andando não pode produzir sequência
// ordenada. Devolver tempo ao id reprova lá, em qualquer codificação.

/** Crockford base32 — 0-9 e A-Z sem I, L, O, U. Exatamente 32 símbolos. */
const ALFABETO = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/** Comprimento do id. 26 × 5 bits = 130 bits. */
const TAMANHO = 26;

export function generateCaseId(): string {
  const bytes = new Uint8Array(TAMANHO);
  crypto.getRandomValues(bytes);
  let id = '';
  for (const b of bytes) {
    // `& 31` toma os 5 bits baixos. 256 é múltiplo de 32 ⇒ distribuição uniforme
    // sem rejeição e sem viés. NÃO trocar por `% 36` nem por qualquer redução que
    // passe por um módulo não-potência-de-2 (mutação M2b provou que enviesa 13,5%).
    id += ALFABETO[b & 31];
  }
  return id;
}
